import React, { useState } from 'react';

import { telemetryClickHandler } from '../telemetryHandler';
import type { DesktopNavItemConfig } from '../types';
import LayeredMenu from './LayeredMenu';
import SingleColumnMenu from './SingleColumnMenu';
import TwoColumnMenu from './TwoColumnMenu';

const AI_GRADIENT_BG = 'linear-gradient(to right, #9952E0 0%, #FF66E6 100%)';

interface DesktopNavItemProps {
  config: DesktopNavItemConfig;
}

export default function DesktopNavItem({
  config,
}: DesktopNavItemProps): JSX.Element {
  const [isOpen, setIsOpen] = useState(false);
  const { dropdown, hasGradientText, gradientIcon, stockblocksGradientIcon } =
    config;
  const resolvedGradientIcon = stockblocksGradientIcon || gradientIcon;

  // Plain link — no dropdown
  if (!dropdown) {
    return (
      <a
        href={config.href}
        className="flex h-10 items-center gap-1 rounded-xl px-3 text-base font-semibold text-gray-900 hover:bg-blue-100 hover:text-blue-600"
        onClick={telemetryClickHandler(
          config.telemetrySlug,
          config.telemetryEvents
        )}
      >
        {config.label}
      </a>
    );
  }

  return (
    <div
      className="relative"
      onMouseEnter={(): void => setIsOpen(true)}
      onMouseLeave={(): void => setIsOpen(false)}
    >
      {/* Trigger pill */}
      <button
        type="button"
        className="flex items-center gap-1 rounded-xl px-3 py-2 text-base font-semibold"
        style={
          isOpen
            ? hasGradientText
              ? {
                  background: AI_GRADIENT_BG,
                  color: '#fff',
                  transition: 'background-color 0.15s, color 0.15s',
                }
              : {
                  backgroundColor: '#dbeafe',
                  color: '#2563eb',
                  transition: 'background-color 0.15s, color 0.15s',
                }
            : { transition: 'background-color 0.15s, color 0.15s' }
        }
      >
        {/* Label */}
        {hasGradientText && !isOpen ? (
          <span className="flex items-center gap-1">
            {resolvedGradientIcon && (
              <img src={resolvedGradientIcon} alt="" width={9} height={9} />
            )}
            <span
              style={{
                background: AI_GRADIENT_BG,
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                color: 'transparent',
              }}
            >
              {config.label}
            </span>
          </span>
        ) : hasGradientText && isOpen ? (
          <span className="flex items-center gap-1">
            {resolvedGradientIcon && (
              <img
                src="/assets/common/images/top-nav/starFourSidedGradientWhite.svg"
                alt=""
                width={9}
                height={9}
              />
            )}
            <span>{config.label}</span>
          </span>
        ) : (
          <span>{config.label}</span>
        )}

        {/* Chevron */}
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          style={{
            transition: 'transform 0.2s',
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
          }}
        >
          <path
            d="M4 6L8 10L12 6"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {/* Dropdown */}
      {isOpen && (
        <>
          {/* Spacer for mouse bridge */}
          <div className="absolute left-0 right-0 z-40 h-7" />

          {/* Dropdown shell */}
          <div className="absolute left-0 top-full z-50 mt-7 overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-md">
            {dropdown.type === 'two-column' && (
              <TwoColumnMenu
                links={dropdown.links}
                cta={dropdown.cta}
                categories={dropdown.categories}
              />
            )}
            {dropdown.type === 'single-column' && (
              <SingleColumnMenu links={dropdown.links} />
            )}
            {dropdown.type === 'layered' && (
              <LayeredMenu sections={dropdown.sections} />
            )}
          </div>
        </>
      )}
    </div>
  );
}
